/**
 * Which direction a section belongs to, and so who may write in it.
 *
 * A section may be given to one or more directions (Finance, Risques...). Only
 * people belonging to one of them may change it, apart from an administrator,
 * who may change anything. A section given to none is open to whoever may edit
 * the report, which is every section of a report written before this existed.
 *
 * A section with no direction of its own takes its chapter's, so giving a
 * chapter to Finance gives everything in it, and a section inside may still be
 * given to another direction by naming it.
 *
 * This is a hint for what to show: the server has the last word on every save.
 */

import { isGroup, type Section } from '@/domain/section'

/** The most a code may be: the server's column. */
const MAX_CODE = 40

/** The most directions one section may carry. */
export const MAX_DIRECTIONS = 12

/**
 * The directions a stored section names, read forgivingly: whatever is not a
 * short, non-empty string is dropped, and repeats collapse. A document written
 * by another version never fails to open over this.
 */
export function readDirections(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  const found: string[] = []
  for (const one of value) {
    if (typeof one !== 'string') continue
    const code = one.trim()
    if (code === '' || code.length > MAX_CODE || found.includes(code)) continue
    found.push(code)
    if (found.length === MAX_DIRECTIONS) break
  }
  return found
}

/**
 * Every section's directions once the chapter's are passed down, by section id.
 *
 * A section's own directions win when it has any; otherwise it has those of
 * the chapter it sits in. Empty means open.
 */
export function effectiveDirections(
  sections: readonly Section[],
  inherited: readonly string[] = [],
  into: Map<string, readonly string[]> = new Map(),
): Map<string, readonly string[]> {
  for (const section of sections) {
    const own = section.directions ?? []
    const effective = own.length > 0 ? own : inherited
    into.set(section.id, effective)
    if (isGroup(section)) effectiveDirections(section.children, effective, into)
  }
  return into
}

/** Who is asking: the directions they belong to, and whether they may write anywhere. */
export interface Writer {
  readonly directions: ReadonlySet<string>
  readonly writesEverywhere: boolean
}

/** Whether this person may change a section given to these directions. */
export function mayWrite(effective: readonly string[], who: Writer): boolean {
  if (effective.length === 0 || who.writesEverywhere) return true
  return effective.some((code) => who.directions.has(code))
}

/**
 * Whether anything in this section, or the section itself, is closed to this
 * person. Deleting or moving a chapter changes what is inside it, so a chapter
 * with one section they may not touch is not theirs to remove.
 */
export function closedWithin(
  section: Section,
  effective: ReadonlyMap<string, readonly string[]>,
  who: Writer,
): boolean {
  if (!mayWrite(effective.get(section.id) ?? [], who)) return true
  return isGroup(section) && section.children.some((child) => closedWithin(child, effective, who))
}
